import { createEvent, createEffect, sample, createStore } from "effector";
import { ModelContract, ModuleDescription } from "../module-description";
import { useRouter } from "next/navigation";
import { $activeRoute } from "@features/app/model";
import { $pageDescriptor } from "@features/shared/page-type";
import { isPageTranslated } from "../../../utils/detect-translator";

type IUseRouter = ReturnType<typeof useRouter> | null;

export const routerInit = createEvent<IUseRouter>();
export const routerPush = createEvent<string>();
// routerPush without the (app) layout's full-screen loader: the current page stays until the
// target renders. For quick jumps into another layout, e.g. /ai-tools → /assistant.
export const routerPushWithoutLoader = createEvent<string>();
export const routerReplace = createEvent<string>();
export const routerReplaceWithoutLoader = createEvent<string>();
export const routerReplaceNoScroll = createEvent<string>();
export const routerBack = createEvent();

export const $router = createStore<IUseRouter>(null).on(routerInit, (_, r) => r);
export const navigationPendingStarted = createEvent<void>();
export const navigationPendingFinished = createEvent<void>();
export const $navigationPending = createStore(false)
    .on(navigationPendingStarted, () => true)
    .on(navigationPendingFinished, () => false);
// Where the pending navigation goes: the layout picks the loading cover from it.
export const $navigationTarget = createStore<string | null>(null)
    .on(
        [routerPush, routerPushWithoutLoader, routerReplace, routerReplaceWithoutLoader, routerReplaceNoScroll],
        (_, url) => url
    )
    .reset(navigationPendingFinished);
// A tab switch raises no loading screen; the shell shows a thin progress bar instead when it drags on.
export const quietNavigationStarted = createEvent<void>();
export const $quietNavigationPending = createStore(false)
    .on(quietNavigationStarted, () => true)
    .reset(navigationPendingFinished);
const $history = createStore<string[]>([])
    .on([routerPush, routerPushWithoutLoader], (s, url) => {
        const prev = typeof window !== "undefined" ? window.location.pathname : "";
        const nextStack = [...s];
        if (prev && nextStack[nextStack.length - 1] !== prev) nextStack.push(prev);
        nextStack.push(url);
        return nextStack;
    })
    .on(routerReplace, (s, url) => (s.length ? [...s.slice(0, -1), url] : [url]))
    .on(routerBack, (s) => (s.length ? s.slice(0, -1) : s));
export const $previousUrl = $history.map((l) => (l.length > 1 ? l[l.length - 2] : null));

type PageMeta = { type: string; metadata: Record<string, any> };
type TypedHistoryEntry = { url: string; page: PageMeta };

export const typedHistoryEntryAdded = createEvent<TypedHistoryEntry>();
export const $historyTyped = createStore<TypedHistoryEntry[]>([]).on(typedHistoryEntryAdded, (s, entry) => {
    const last = s[s.length - 1];
    if (last && last.url === entry.url && last.page.type === entry.page.type) return s;
    return [...s, entry];
});

// Preview host is inert: it must never navigate — it lives inside the admin
// editor iframe (see app/upsell-preview). All Effector-driven navigation is
// centralized in the four effects below, so this one guard covers every
// redirect source in the FEATURES graph (auth bounces, first-login upsell
// redirects, error fallbacks, post-purchase hops).
const isInertPreviewHost = (): boolean =>
    typeof window !== "undefined" && window.location.pathname.startsWith("/upsell-preview");

const isSamePathname = (currentUrl: string, targetUrl: string): boolean => {
    try {
        const current = new URL(currentUrl, window.location.origin);
        const target = new URL(targetUrl, window.location.origin);
        return current.pathname === target.pathname && current.search !== target.search;
    } catch {
        return false;
    }
};

const pushFx = createEffect(({ url, router }: { url: string; router: IUseRouter }) => {
    if (isInertPreviewHost()) return;
    if (isPageTranslated() && typeof window !== "undefined") {
        if (isSamePathname(window.location.href, url)) {
            window.history.replaceState({}, "", url);
            return;
        }
        window.location.href = url;
        return;
    }
    router?.push(url);
});
const replaceFx = createEffect(({ url, router }: { url: string; router: IUseRouter }) => {
    if (isInertPreviewHost()) return;
    if (isPageTranslated() && typeof window !== "undefined") {
        if (isSamePathname(window.location.href, url)) {
            window.history.replaceState({}, "", url);
            return;
        }
        window.location.replace(url);
        return;
    }
    router?.replace(url);
});
const replaceNoScrollFx = createEffect(({ url, router }: { url: string; router: IUseRouter }) => {
    if (isInertPreviewHost()) return;
    if (isPageTranslated() && typeof window !== "undefined") {
        if (isSamePathname(window.location.href, url)) {
            window.history.replaceState({}, "", url);
            return;
        }
        window.location.replace(url);
        return;
    }
    router?.replace(url, { scroll: false });
});
const backFx = createEffect(({ router, previousUrl }: { router: IUseRouter; previousUrl: string | null }) => {
    if (isInertPreviewHost()) return;
    if (isPageTranslated() && typeof window !== "undefined") {
        if (previousUrl) {
            window.location.href = previousUrl;
        } else {
            window.history.go(-1);
        }
        return;
    }
    router?.back();
});

const normalizePathname = (url: string) => {
    if (!url) return url;
    const queryIndex = url.indexOf("?");
    const hashIndex = url.indexOf("#");
    const endIndex = Math.min(queryIndex === -1 ? url.length : queryIndex, hashIndex === -1 ? url.length : hashIndex);
    return url.slice(0, endIndex);
};

sample({
    clock: [routerPush, routerPushWithoutLoader],
    source: $router,
    filter: Boolean,
    fn: (router, url) => ({ url, router }),
    target: pushFx
});
sample({
    clock: [routerReplace, routerReplaceWithoutLoader],
    source: $router,
    filter: Boolean,
    fn: (router, url) => ({ url, router }),
    target: replaceFx
});
sample({
    clock: routerReplaceNoScroll,
    source: $router,
    filter: Boolean,
    fn: (router, url) => ({ url, router }),
    target: replaceNoScrollFx
});
sample({
    clock: routerBack,
    source: { router: $router, previousUrl: $previousUrl },
    fn: ({ router, previousUrl }) => ({ router, previousUrl }),
    target: backFx
});

sample({
    clock: routerPush,
    source: { current: $activeRoute, pending: $navigationPending, router: $router },
    filter: ({ current, pending, router }, url) => !!router && !pending && normalizePathname(url) !== current,
    fn: () => undefined,
    target: navigationPendingStarted
});

sample({
    clock: routerReplace,
    source: { current: $activeRoute, pending: $navigationPending, router: $router },
    filter: ({ current, pending, router }, url) => !!router && !pending && normalizePathname(url) !== current,
    fn: () => undefined,
    target: navigationPendingStarted
});

sample({
    clock: routerReplaceNoScroll,
    source: { current: $activeRoute, pending: $navigationPending, router: $router },
    filter: ({ current, pending, router }, url) => !!router && !pending && normalizePathname(url) !== current,
    fn: () => undefined,
    target: navigationPendingStarted
});

sample({
    clock: [routerPushWithoutLoader, routerReplaceWithoutLoader],
    source: { current: $activeRoute, router: $router },
    filter: ({ current, router }, url) => !!router && normalizePathname(url) !== current,
    fn: () => undefined,
    target: quietNavigationStarted
});

sample({
    clock: $pageDescriptor,
    source: $activeRoute,
    fn: (url, page) => ({ url, page }) as TypedHistoryEntry,
    target: typedHistoryEntryAdded
});

const contract = new ModelContract(
    { $previousUrl, $history, $historyTyped, $navigationPending, $navigationTarget, $quietNavigationPending },
    {
        routerPush,
        routerPushWithoutLoader,
        routerReplace,
        routerReplaceWithoutLoader,
        routerReplaceNoScroll,
        routerBack,
        navigationPendingStarted,
        navigationPendingFinished
    },
    {},
    {}
);

export type IRouterContract = typeof contract;

export class RouterModule extends ModuleDescription<IRouterContract> {
    getDefaultContract() {
        return contract;
    }
}
